import { Vec2 } from "../../models/SyntheticMap";
import { BaseService } from "./BaseService";
import * as THREE from "three";
import { alignObject, ensureChild } from "../../visuals/visual-utils";
import { sleep } from "../../utils/ioUtils";
import { deleteOrgBlob, downloadOrgBlob, uploadOrgBlob } from "../common/UploadBlob";
import { OrgBlobType } from "../../models/OrgBlob";
import { WREvents } from "../WREvents";

const MESH_HOLE_CODE = -100000;

export class GridService extends BaseService {
  private gridUnitSize: Vec2 = { x: 1000, y: 1000 };
  private gridSize: Vec2 = { x: 1000, y: 1000 };
  private resolution: number = 1000; // 1km resolution
  private gridOffset: Vec2 = { x: 0, y: 0 };
  private elevations: number[] = [];
  private normals: (THREE.Vector3 | null)[] = [];
  private bufferSize: number = 0;
  render(delta: number): void {
  }

  public async loadForActiveSite() {
    this.setupForActiveSite();
    const site = this.worldRenderer.dataService.getActiveSite();
    if (site.grid.blobId) {
      const data = await downloadOrgBlob(site.grid.blobId);
      const buffer = new Float32Array(data);
      this.elevations = Array.from(buffer);
      if (this.elevations.length !== this.bufferSize) {
        this.elevations = new Array(this.bufferSize).fill(0); // Reset to default if size mismatch
        console.warn(`GridService: Elevation data size mismatch. Expected ${this.bufferSize}, got ${this.elevations.length}`);
      }
      this.normals = new Array(this.bufferSize).fill(null); // Reset normals
    }
  }

  public async buildGrid() {
    this.setupForActiveSite(); // Initialize normals with null
    this.elevations = new Array(this.bufferSize).fill(0); // Initialize grid with zeros
    this.normals = new Array(this.bufferSize).fill(null); // Initialize normals with null
    const site = this.worldRenderer.dataService.getActiveSite();
    const planet = this.worldRenderer.planetService;
    const parent = site.obj
    const scanAlt = 30; // Altitude for scanning
    const scanner = new THREE.Mesh(
      new THREE.SphereGeometry(this.resolution, 8, 8),
      new THREE.MeshBasicMaterial({ color: 0x0000ff })
    );
    scanner.name = 'gridScanner';
    parent.add(scanner);
    scanner.position.set(0, 0, scanAlt);
    const gridMarkers = ensureChild(parent, 'gridMarkers', THREE.Group);
    // return;
    for (let i = 0; i < this.elevations.length; i++) {
      const pos = this.getPositionAtIndex(i, scanAlt);
      scanner.position.copy(pos);
      scanner.updateMatrixWorld();
      const wpos = scanner.getWorldPosition(new THREE.Vector3());
      const direction = wpos.clone().multiplyScalar(-1).normalize();
      const raycaster = new THREE.Raycaster(wpos, direction);
      raycaster.far = 10000; // Set a reasonable distance for raycasting
      const intersects = raycaster.intersectObjects(planet.worldGroup.children, true);
      if (intersects.length === 0) {
        this.elevations[i] = MESH_HOLE_CODE; // No intersection found, set elevation to 0
        console.warn(`No intersection found for grid index ${i} at position ${pos.x}, ${pos.y}`);
        continue; // Skip if no intersection found
      }
      const point = intersects[0].point;
      const elevation = scanAlt - point.distanceTo(wpos); // Calculate elevation based on intersection point
      this.elevations[i] = elevation; // Store the elevation in the grid
      const marker = new THREE.Mesh(
        new THREE.SphereGeometry(1, 8, 8),
        new THREE.MeshBasicMaterial({ color: 0xff0000 })
      );
      const axis = new THREE.AxesHelper(this.resolution * 0.5);
      axis.position.z = 0.25;
      marker.add(axis);
      marker.position.copy(pos);
      marker.position.z = elevation; // Adjust height for visibility
      gridMarkers.add(marker);
      await sleep(1); // Wait for the scanner to update its position
    }
    for(let i = 0; i < this.elevations.length; i++) {
      const marker = gridMarkers.children[i] as THREE.Mesh;
      const { elevation, normal } = this.getElevationAndNormal(marker.position);
      const normalArrow = new THREE.ArrowHelper(
        normal,
        marker.position,
        this.resolution,
        0x00ff00
      );
      gridMarkers.add(normalArrow);
      alignObject(marker, normal);
    }
    parent.remove(scanner);
    if (site.grid.blobId) {
      await deleteOrgBlob(site.grid.blobId);
    }
    // convert elevations to an array buffer
    const buffer = new Float32Array(this.elevations);
    const blob = await uploadOrgBlob(OrgBlobType.ElevationGrid, buffer.buffer);
    site.grid.blobId = blob._id;
    this.worldRenderer.emitEvent(WREvents.SiteUpdated, site);
    setTimeout(() => {
      gridMarkers.clear();
    }, 10000);
  }

  private setupForActiveSite() {
    const site = this.worldRenderer.dataService.getActiveSite();
    const { size, resolution } = site.grid;
    this.resolution = resolution;
    this.gridSize = {
      x: size,
      y: size,
    };
    this.gridUnitSize = {
      x: Math.round(size / resolution) + 1,
      y: Math.round(size / resolution) + 1,
    };
    this.gridOffset = {
      x: this.gridSize.x / 2,
      y: this.gridSize.y / 2,
    };
    this.bufferSize = this.gridUnitSize.x * this.gridUnitSize.y;
  }

  public getGridValue(pos: Vec2): number {
    const { index } = this.getIndex(pos);
    if (index < 0 || index >= this.elevations.length) {
      return 0; // Out of bounds, return default value
    }
    return this.elevations[index] || 0; // Return the value or 0 if not
  }

  public setGridValue(pos: Vec2, value: number) {
    const { index } = this.getIndex(pos);
    if (index < 0 || index >= this.elevations.length) {
      return
    }
    this.elevations[index] = value; // Set the value
  }

  private getInGrid(pos: Vec2): number {
    return this.elevations[
      pos.y * this.gridUnitSize.x + pos.x
    ]
  }

  public getElevationAndNormal(pos: Vec2): { elevation: number, normal: THREE.Vector3 } {
    const { index, inGrid } = this.getIndex(pos);
    if (index < 0 || index >= this.elevations.length) {
      return { elevation: 0, normal: new THREE.Vector3(0, 0, 1) }; // Default elevation and normal
    }
    let elevation = this.elevations[index];
    if (this.normals[index]) {
      return { elevation, normal: this.normals[index]! }; // Return existing normal
    }
    const nX = inGrid.x === 0 ? 
      { x: inGrid.x + 1, y: inGrid.y } : 
      { x: inGrid.x - 1, y: inGrid.y };

    const nY = inGrid.y === 0 ? 
      { x: inGrid.x, y: inGrid.y + 1 } : 
      { x: inGrid.x, y: inGrid.y - 1 };

    let eX = this.getInGrid(nX);
    let eY = this.getInGrid(nY);


    const goodData = [elevation, eX, eY].filter(e => e !== MESH_HOLE_CODE);
    if (goodData.length === 0) {
      return { elevation: 0, normal: new THREE.Vector3(0, 0, 1) }; // No valid data, return default
    }
    const avgElevation = goodData.reduce((sum, val) => sum + val, 0) / goodData.length;
    if (elevation === MESH_HOLE_CODE) {
      elevation = avgElevation; // Use average if current elevation is a hole
      this.elevations[index] = elevation; // Use average if current elevation is a hole
    }
    if (eX === MESH_HOLE_CODE) {
      eX = avgElevation; // Use average if neighbor elevation is a hole
    }
    if (eY === MESH_HOLE_CODE) {
      eY = avgElevation; // Use average if neighbor elevation is a hole
    }
    const plane = [
      new THREE.Vector3(0, 0, elevation),
      new THREE.Vector3(inGrid.x === 0 ? this.resolution : -this.resolution, 0, eX),
      new THREE.Vector3(0, inGrid.y === 0 ? this.resolution : -this.resolution, eY)
    ];
    // calculate normal from the plane
    const v1 = plane[1].clone().sub(plane[0]);
    const v2 = plane[2].clone().sub(plane[0]);
    const n = new THREE.Vector3().crossVectors(v1, v2).normalize();
    if (n.z < 0) {
      n.negate(); // Ensure the normal points upwards
    }
    this.normals[index] = n; // Store the normal for future use
    return { elevation, normal: n };
  }

  private getIndex(pos: Vec2): { index: number, inGrid: Vec2 } {
    const inGrid: Vec2 = {
      x: Math.round((pos.x + this.gridOffset.x) / this.resolution),
      y: Math.round((pos.y + this.gridOffset.y) / this.resolution),
    };
    if (inGrid.x < 0 || inGrid.y < 0 || inGrid.x >= this.gridUnitSize.x || inGrid.y >= this.gridUnitSize.y) {
      return { index: -1, inGrid }; // Out of bounds
    }
    return { index: inGrid.y * this.gridUnitSize.x + inGrid.x, inGrid };
  }

  private getPositionAtIndex(index: number, z: number): THREE.Vector3 {
    const y = Math.floor(index / this.gridUnitSize.x);
    const x = index % this.gridUnitSize.x;
    return new THREE.Vector3(
      x * this.resolution - this.gridOffset.x,
      y * this.resolution - this.gridOffset.y,
      z
    );
  }
}